<script setup>
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import MultiChannelCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/MultiChannelCampaign/MultiChannelCampaignDialog.vue';

const { t } = useI18n();
const store = useStore();

const [showDialog, toggleDialog] = useToggle();

const uiFlags = useMapGetter('multiChannelCampaigns/getUIFlags');
const campaigns = useMapGetter('multiChannelCampaigns/getCampaigns');

const isFetching = computed(() => uiFlags.value.isFetching);
const hasNoCampaigns = computed(
  () => campaigns.value?.length === 0 && !isFetching.value
);

onMounted(() => {
  store.dispatch('multiChannelCampaigns/get');
});

const handleDelete = async campaign => {
  try {
    await store.dispatch('multiChannelCampaigns/delete', campaign.id);
    useAlert(t('CAMPAIGN.MULTI_CHANNEL.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('CAMPAIGN.MULTI_CHANNEL.DELETE.API.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.MULTI_CHANNEL.HEADER_TITLE')"
    :button-label="t('CAMPAIGN.MULTI_CHANNEL.NEW_CAMPAIGN')"
    @click="toggleDialog()"
    @close="toggleDialog(false)"
  >
    <template #action>
      <MultiChannelCampaignDialog
        v-if="showDialog"
        @close="toggleDialog(false)"
      />
    </template>

    <div
      v-if="isFetching"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>

    <div
      v-else-if="hasNoCampaigns"
      class="flex flex-col items-center justify-center gap-1 pt-14 text-center"
    >
      <p class="text-base font-medium text-n-slate-12">
        {{ t('CAMPAIGN.MULTI_CHANNEL.EMPTY_STATE.TITLE') }}
      </p>
      <p class="text-sm text-n-slate-11">
        {{ t('CAMPAIGN.MULTI_CHANNEL.EMPTY_STATE.SUBTITLE') }}
      </p>
    </div>

    <ul v-else class="flex flex-col gap-3 list-none">
      <li
        v-for="campaign in campaigns"
        :key="campaign.id"
        class="flex items-start justify-between gap-3 p-4 border rounded-xl border-n-weak bg-n-alpha-2"
      >
        <div class="flex flex-col min-w-0 gap-1">
          <span class="text-sm font-medium truncate text-n-slate-12">
            {{ campaign.title }}
          </span>
          <span class="text-sm truncate text-n-slate-11">
            {{ campaign.default_message }}
          </span>
          <div class="flex items-center gap-2 mt-1 text-xs text-n-slate-10">
            <span class="px-2 py-0.5 rounded-md bg-n-alpha-3">
              {{ campaign.status }}
            </span>
            <span>
              {{
                t('CAMPAIGN.MULTI_CHANNEL.CARD.CHANNELS', {
                  count: campaign.inboxes ? campaign.inboxes.length : 0,
                })
              }}
            </span>
            <span>· {{ campaign.routing_strategy }}</span>
          </div>
        </div>
        <Button
          variant="ghost"
          color="ruby"
          size="sm"
          icon="i-lucide-trash"
          @click="handleDelete(campaign)"
        />
      </li>
    </ul>
  </CampaignLayout>
</template>
